// FieldDesk web-dispatcher — основной оркестратор UI после авторизации.
//
// Две ветки:
//   - Embedded + deal placement → EmbeddedDealView (один экран, минимум chrome).
//   - Standalone (или embedded без deal-контекста) → полный интерфейс
//     с Sidebar + 5 экранов.

import { useState } from 'react';
import type { ScreenId } from './App';
import type { AuthUser, VisitDto } from './api/types';
import { Sidebar } from './components/Sidebar';
import { NewTaskModal } from './components/NewTaskModal';
import { DashboardScreen } from './screens/Dashboard';
import { ScheduleScreen } from './screens/Schedule';
import { EngineersScreen } from './screens/Engineers';
import { ClientsScreen } from './screens/Clients';
import { ReportsScreen } from './screens/Reports';
import { SettingsScreen } from './screens/Settings';
import { MapScreen } from './screens/Map';
import { EmbeddedDealView } from './screens/EmbeddedDeal';
import { VisitDetailDrawer } from './screens/VisitDetail';
import { useAuth } from './auth/AuthContext';

const SIDEBAR_COLLAPSED_KEY = 'fd.sidebarCollapsed';

interface Props {
  user: AuthUser;
  embedded: boolean;
  dealId: string | null;
}

export function AppShell({ user, embedded, dealId }: Props) {
  const { logout } = useAuth();
  // ENGINEER = полные права DISPATCHER: те же экраны, создание/редактирование/
  // отмена любых выездов. Отличия только у ADMIN (Инженеры/Отчёты) и
  // SUPER_ADMIN (Настройки).
  const isAdmin = user.role === 'ADMIN';
  const isSuperAdmin = user.isSuperAdmin === true;
  const [screen, setScreen] = useState<ScreenId>('dashboard');
  const [modalOpen, setModalOpen] = useState(false);
  const [openVisitId, setOpenVisitId] = useState<number | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  // Свёрнутость левой панели запоминаем в браузере (в iframe Б24 localStorage
  // может быть недоступен — тогда просто не сохраняем).
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  function toggleSidebar() {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* storage недоступен — состояние живёт до перезагрузки */
      }
      return next;
    });
  }

  // Список разрешённых экранов в зависимости от роли. Если пытается
  // открыть скрытый экран — фолбэк на 'map'.
  //   ENGINEER / DISPATCHER: Обзор, Карта, Расписание, Клиенты.
  //   ADMIN:       всё выше + Инженеры + Отчёты.
  //   SUPER_ADMIN: всё выше + Настройки.
  const allowedScreens: readonly ScreenId[] = isAdmin
    ? (isSuperAdmin
        ? (['dashboard', 'map', 'schedule', 'engineers', 'clients', 'reports', 'settings'] as const)
        : (['dashboard', 'map', 'schedule', 'engineers', 'clients', 'reports'] as const))
    : (['dashboard', 'map', 'schedule', 'clients'] as const);
  const safeScreen: ScreenId = allowedScreens.includes(screen) ? screen : 'map';

  function handleOpen(v: VisitDto) {
    setOpenVisitId(v.id);
  }

  // --- Embedded + deal placement: однопанельный режим ---
  if (embedded && dealId) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            padding: '10px 16px',
            background: '#0f172a',
            color: '#e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: '#60a5fa', letterSpacing: '1.5px' }}>
            FIELDDESK · сделка #{dealId}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{user.fullName}</div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <EmbeddedDealView key={reloadTick} dealId={dealId} onOpenVisit={handleOpen} />
        </div>
        {openVisitId != null && (
          <VisitDetailDrawer
            visitId={openVisitId}
            onClose={() => {
              setOpenVisitId(null);
              setReloadTick((t) => t + 1);
            }}
          />
        )}
      </div>
    );
  }

  // --- Standalone / embedded без deal: полный UI ---
  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <Sidebar
        active={safeScreen}
        onNav={setScreen}
        onAdd={() => setModalOpen(true)}
        onLogout={!embedded ? logout : undefined}
        user={user}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
      />
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', position: 'relative' }}>
        <div key={`${safeScreen}-${reloadTick}`} style={{ height: '100%' }}>
          {/* Защита: рендерим только разрешённые экраны. Если screen из
              хранилища ENGINEER попал на 'reports' — safeScreen упадёт
              на 'map'. Дополнительно проверяем на месте. */}
          {safeScreen === 'dashboard' && allowedScreens.includes('dashboard') && (
            <DashboardScreen onAdd={() => setModalOpen(true)} onOpenVisit={handleOpen} />
          )}
          {safeScreen === 'schedule' && <ScheduleScreen onOpenVisit={handleOpen} />}
          {safeScreen === 'engineers' && allowedScreens.includes('engineers') && (
            <EngineersScreen onOpenVisit={handleOpen} />
          )}
          {safeScreen === 'clients' && allowedScreens.includes('clients') && (
            <ClientsScreen onOpenVisit={handleOpen} />
          )}
          {safeScreen === 'reports' && allowedScreens.includes('reports') && (
            <ReportsScreen onOpenVisit={handleOpen} />
          )}
          {safeScreen === 'settings' && allowedScreens.includes('settings') && (
            <SettingsScreen />
          )}
          {safeScreen === 'map' && <MapScreen />}
        </div>
      </div>

      {modalOpen && (
        <NewTaskModal
          onClose={() => setModalOpen(false)}
          onCreated={() => {
            setModalOpen(false);
            setReloadTick((t) => t + 1);
          }}
          // Sprint UX-fix: ad-hoc разрешён всем ролям (ENGINEER = права
          // DISPATCHER). Embedded+deal сюда не попадает: ранний return выше.
          allowAdHoc
        />
      )}

      {openVisitId != null && (
        <VisitDetailDrawer
          visitId={openVisitId}
          onClose={() => {
            setOpenVisitId(null);
            setReloadTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}
